import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, Send, X, Sparkles, Loader2, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const chatApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const STORAGE_KEY = "hq_support_session";

export function ChatWidget({ handle }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t(lang, "chat_welcome"), at: new Date().toISOString() }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", text: msg, at: now }]);
    setBusy(true);
    try {
      const { data } = await chatApi.post("/support/chat", {
        session_id: sessionId,
        message: msg,
        language_hint: lang,
        handle,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      setMessages((m) => [...m, {
        role: "assistant",
        text: data.reply,
        at: new Date().toISOString(),
        escalated: data.escalated,
      }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: t(lang, "chat_error"),
        at: new Date().toISOString(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[color:var(--hq-cyan)] text-black flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
          aria-label={t(lang, "chat_open")}
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget"
          className="fixed bottom-5 right-5 z-40 w-[92vw] max-w-[380px] h-[560px] max-h-[85vh] bg-[#0a0a0a] border border-white/10 flex flex-col shadow-2xl rounded-md overflow-hidden"
        >
          <div className="p-3 border-b border-white/10 flex items-center gap-2 bg-black/40">
            <Sparkles size={14} className="text-[color:var(--hq-cyan)]" />
            <div className="flex-1">
              <div className="text-sm font-display">{t(lang, "chat_title")}</div>
              <div className="text-[10px] font-mono uppercase text-white/40">GPT-5.2 · EMRKT.CH</div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              className="text-white/50 hover:text-white p-1"
            >
              <X size={14} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed rounded-md whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[color:var(--hq-cyan)]/20 text-white border border-[color:var(--hq-cyan)]/30"
                      : "bg-white/5 text-white/90 border border-white/10"
                  }`}
                >
                  {m.text}
                  {m.escalated && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] font-mono uppercase text-[color:var(--hq-warning)]">
                      <AlertTriangle size={10} /> {t(lang, "chat_escalated")}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 px-3 py-2 rounded-md text-white/60 text-xs">
                  <Loader2 size={12} className="inline animate-spin mr-2" />
                  {t(lang, "chat_thinking")}
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2 bg-black/40">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={t(lang, "chat_placeholder")}
              className="hq-input flex-1"
              disabled={busy}
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              className="hq-btn hq-btn-primary disabled:opacity-50"
              aria-label={t(lang, "chat_send")}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
